import { showAlert } from '@/components/ui/alert';
import { STRINGS } from '@/constants/strings';
import { clearBiometricCredential } from '@/features/auth/services/biometric-credentials';
import { useBiometricStore } from '@/features/auth/stores/biometric-store';
import { shouldSuppressErrorAlert } from '@/libs/errors/request-timeout';
import { googleSignOut, resetCurrentPassword } from '@/libs/firebase/auth';
import type { GuardianStackParamList } from '@/navigations/types';
import { useSessionStore } from '@/stores/session-store';
import { useUiStore } from '@/stores/ui-store';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useCallback, useState } from 'react';
import { useShallow } from 'zustand/shallow';

type FieldKey = 'currentPassword' | 'newPassword' | 'confirmPassword';

const strings = STRINGS.guardian.changePassword;

export function useGuardianChangePassword() {
  const navigation = useNavigation<NativeStackNavigationProp<GuardianStackParamList>>();
  const user = useSessionStore(state => state.user);
  const { showGlobalLoading, hideGlobalLoading } = useUiStore(
    useShallow(s => ({
      showGlobalLoading: s.showGlobalLoading,
      hideGlobalLoading: s.hideGlobalLoading,
    })),
  );

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmMismatchError, setConfirmMismatchError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});

  const handleCancel = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const handleUpdatePassword = useCallback(async () => {
    const nextErrors: Partial<Record<FieldKey, string>> = {};

    if (currentPassword.length < 8) {
      nextErrors.currentPassword = strings.validationMinLength;
    }

    if (newPassword.length < 8) {
      nextErrors.newPassword = strings.validationMinLength;
    }

    if (confirmPassword.length < 8) {
      nextErrors.confirmPassword = strings.validationMinLength;
    }

    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    if (newPassword !== confirmPassword) {
      setConfirmMismatchError(strings.validationConfirmMismatch);
      return;
    }
    setConfirmMismatchError(null);

    const email = user?.email?.trim();
    if (!email) {
      showAlert({
        title: STRINGS.common.noticeTitle,
        message: STRINGS.auth.changePassword.errorSessionExpired,
        buttons: [{ text: STRINGS.common.buttons.dismiss }],
      });
      return;
    }

    try {
      showGlobalLoading();
      await resetCurrentPassword({
        email,
        currentPassword,
        newPassword,
      });

      // Security: the password just changed, so the stored biometric credential (tied to the OLD
      // password/token) is now stale. Invalidate it for THIS account — remove the Keychain secret
      // and the biometric-store binding — so the login screen no longer offers biometric unlock.
      // The user must sign in with the new password, then re-opt into biometrics to re-enable it.
      const uid = user?.uid;
      if (uid) {
        await clearBiometricCredential(uid);
        useBiometricStore.getState().disable(uid);
      }

      showAlert({
        title: STRINGS.common.noticeTitle,
        message: STRINGS.auth.changePassword.successMessage,
        buttons: [
          {
            text: STRINGS.common.buttons.dismiss,
            onPress: () => {
              googleSignOut();
              useSessionStore.getState().setSoftLoggedOut(true);
            },
          },
        ],
      });
    } catch (e) {
      if (shouldSuppressErrorAlert(e)) {
        return;
      }

      const err = e as { code?: string };
      const code = err?.code ?? '';
      const isCurrentPasswordIncorrect =
        code === 'auth/wrong-password' || code === 'auth/invalid-credential';

      if (isCurrentPasswordIncorrect) {
        setFieldErrors(prev => ({
          ...prev,
          currentPassword: strings.errorCurrentPassword,
        }));
        return;
      }

      showAlert({
        title: STRINGS.common.noticeTitle,
        message: STRINGS.errors.firestoreTimeout,
        buttons: [{ text: STRINGS.common.buttons.dismiss }],
      });
    } finally {
      hideGlobalLoading();
    }
  }, [
    confirmPassword,
    currentPassword,
    hideGlobalLoading,
    newPassword,
    showGlobalLoading,
    user?.email,
    user?.uid,
  ]);

  const onChangeCurrentPassword = useCallback((text: string) => {
    setCurrentPassword(text);
    setFieldErrors(prev => (prev.currentPassword ? { ...prev, currentPassword: undefined } : prev));
  }, []);

  const onChangeNewPassword = useCallback(
    (text: string) => {
      setNewPassword(text);
      if (confirmMismatchError) setConfirmMismatchError(null);
      setFieldErrors(prev => (prev.newPassword ? { ...prev, newPassword: undefined } : prev));
    },
    [confirmMismatchError],
  );

  const onChangeConfirmPassword = useCallback(
    (text: string) => {
      setConfirmPassword(text);
      if (confirmMismatchError) setConfirmMismatchError(null);
      setFieldErrors(prev =>
        prev.confirmPassword ? { ...prev, confirmPassword: undefined } : prev,
      );
    },
    [confirmMismatchError],
  );

  return {
    currentPassword,
    newPassword,
    confirmPassword,
    confirmMismatchError,
    fieldErrors,
    onChangeCurrentPassword,
    onChangeNewPassword,
    onChangeConfirmPassword,
    handleCancel,
    handleUpdatePassword,
  };
}
